import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Tabs, Tab, TextField,
  Stack, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Typography,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../api/endpoints/categories';
import ConfirmDialog from '../../components/ConfirmDialog';

const CategoryDialog = ({ open, onClose, type = 'blog', onSaved }) => {
  const [tab, setTab] = useState(type === 'product' ? 0 : 1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'blog', description: '' });

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const catType = tab === 0 ? 'product' : 'blog';
      const res = await getCategories({ type: catType });
      setCategories(res.data.data || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (open) fetchCategories();
  }, [open, fetchCategories]);

  const resetForm = () => {
    setForm({ name: '', type: tab === 0 ? 'product' : 'blog', description: '' });
    setEditing(null);
  };

  const handleEdit = (cat) => {
    setForm({ name: cat.name, type: cat.type, description: cat.description || '' });
    setEditing(cat._id);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Name is required');
      return;
    }
      const catType = tab === 0 ? 'product' : 'blog';
    try {
      if (editing) {
        await updateCategory(editing, form);
        toast.success('Category updated');
      } else {
        await createCategory({...form ,type:catType});
        toast.success('Category created');
      }
      resetForm();
      fetchCategories();
      onSaved?.();
    } catch {
      toast.error('Failed to save category');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteId);
      toast.success('Category deleted');
      setDeleteId(null);
      fetchCategories();
      onSaved?.();
    } catch {
      toast.error('Failed to delete category');
    }
  };

  const handleTabChange = (e, newTab) => {
    setTab(newTab);
    resetForm();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Categories</DialogTitle>
        <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="Product Categories" />
          <Tab label="Blog Categories" />
        </Tabs>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField label="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} size="small" sx={{ flex: 1 }} />
              <TextField label="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} size="small" sx={{ flex: 1 }} />
              <Button variant="contained" size="small" startIcon={editing ? <EditIcon /> : <AddIcon />} onClick={handleSave}>
                {editing ? 'Update' : 'Add'}
              </Button>
              {editing && <Button size="small" onClick={resetForm}>Cancel</Button>}
            </Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
            ) : categories.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={3}>No categories found</Typography>
            ) : (
              <List dense>
                {categories.map((cat) => (
                  <ListItem key={cat._id} divider>
                    <ListItemText primary={cat.name} secondary={cat.description} />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => handleEdit(cat)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setDeleteId(cat._id)}><DeleteIcon fontSize="small" /></IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={!!deleteId} title="Delete Category" message="Are you sure?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </>
  );
};

export default CategoryDialog;
